import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Compass } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Not-found page.
 *
 * Rendered for any unknown path inside a localised route tree:
 *   /ar/anything-unknown → Arabic 404 (RTL)
 *   /en/anything-unknown → English 404 (LTR)
 *
 * Unknown top-level paths (e.g. /foobar) are forwarded from `App.js` to
 * `/ar/foobar` so the original mistyped URL is preserved in the address bar
 * and this same page renders in Arabic (the site default).
 *
 * Design goals — minimalist, consistent with the rest of SAS:
 *   • white background, black text, restrained purple accent
 *   • single centered column, generous whitespace
 *   • small eyebrow mark for brand continuity
 *   • two primary actions (home + tools) plus a quiet “report a broken link”
 */

const C = {
  ar: {
    eyebrow: "404 · غير موجود",
    title: "الصفحة غير موجودة",
    lead: "قد يكون الرابط قد انتقل أو لم يعد متاحاً. يمكنكم العودة إلى الصفحة الرئيسية أو استكشاف الأدوات.",
    triedLabel: "الرابط المطلوب",
    home: "العودة إلى الرئيسية",
    tools: "استكشاف الأدوات",
    reportLink: "الإبلاغ عن رابط معطّل",
    docTitle: "الصفحة غير موجودة · 404 | مجتمع الذكاء الاصطناعي السوري",
  },
  en: {
    eyebrow: "404 · Not found",
    title: "This page doesn’t exist",
    lead: "The link may have moved or no longer be available. You can head back to the homepage or explore our tools.",
    triedLabel: "Requested URL",
    home: "Back to home",
    tools: "Explore the tools",
    reportLink: "Report a broken link",
    docTitle: "Page not found · 404 | Syrian AI Society",
  },
};

export default function NotFound() {
  const { lang } = useLanguage();
  const c = C[lang];
  const { pathname } = useLocation();

  // In RTL the visual “back” arrow should face the language’s start edge,
  // so swap the icon direction per language. Both point toward “home”.
  const HomeArrow = lang === "ar" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    const previous = document.title;
    document.title = c.docTitle;
    return () => {
      document.title = previous;
    };
  }, [c.docTitle]);

  return (
    <section
      className="sas-section"
      data-testid="not-found-page"
      aria-labelledby="not-found-heading"
    >
      <div className="sas-container">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="sas-hero-mark" aria-hidden />
            <span className="sas-eyebrow" data-testid="not-found-eyebrow">
              {c.eyebrow}
            </span>
          </div>

          <h1
            id="not-found-heading"
            className="sas-h1"
            data-testid="not-found-title"
          >
            {c.title}
          </h1>

          <p
            className="sas-body mt-5 text-[15.5px]"
            data-testid="not-found-lead"
          >
            {c.lead}
          </p>

          {pathname && (
            <p
              className="mt-7 text-[12px] text-[color:var(--sas-ink-2)] flex items-center justify-center gap-2 flex-wrap"
              data-testid="not-found-tried"
            >
              <span className="opacity-70">{c.triedLabel}:</span>
              <code
                className="font-mono px-2 py-0.5 rounded bg-[color:var(--sas-accent-soft)]/60 text-[color:var(--sas-ink)] break-all"
                dir="ltr"
              >
                {pathname}
              </code>
            </p>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-3 justify-center">
            <LocalizedLink
              to="/"
              className="sas-btn sas-btn-primary"
              data-testid="not-found-home"
            >
              <HomeArrow size={16} aria-hidden />
              {c.home}
            </LocalizedLink>
            <LocalizedLink
              to="/tools"
              className="sas-btn sas-btn-ghost"
              data-testid="not-found-tools"
            >
              <Compass size={16} aria-hidden />
              {c.tools}
            </LocalizedLink>
          </div>

          <div className="mt-8 text-[12.5px]">
            <LocalizedLink
              to="/contact/general"
              className="text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-accent)] underline-offset-4 hover:underline"
              data-testid="not-found-report"
            >
              {c.reportLink}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </section>
  );
}
